import { useEffect, useState } from 'react';
import { getCsvCell, parseNumber, type CsvRecord } from './dataExchange';

export type DistanceUnit = 'mi' | 'km';

export const KM_PER_MILE = 1.609344;
export const DISTANCE_UNIT_CHANGE_EVENT = 'fleetfix-distance-unit-change';

const normalizeDistanceUnit = (value: unknown): DistanceUnit => {
  if (value === 'km' || value === 'kilometer' || value === 'kilometers') {
    return 'km';
  }

  return 'mi';
};

export const getSavedDistanceUnit = (): DistanceUnit => {
  if (typeof window === 'undefined') {
    return 'mi';
  }

  try {
    const savedFleetSettings = window.localStorage.getItem('fleetSettings');
    if (!savedFleetSettings) {
      return 'mi';
    }

    const parsed = JSON.parse(savedFleetSettings);
    return normalizeDistanceUnit(parsed?.distanceUnit);
  } catch {
    return 'mi';
  }
};

export const toDisplayDistance = (miles: number | null | undefined, unit: DistanceUnit = getSavedDistanceUnit()): number => {
  const safeMiles = Number.isFinite(Number(miles)) ? Number(miles) : 0;
  return unit === 'km' ? safeMiles * KM_PER_MILE : safeMiles;
};

export const fromDisplayDistance = (value: number | string | null | undefined, unit: DistanceUnit = getSavedDistanceUnit()): number => {
  const numericValue = Number(value);
  const safeValue = Number.isFinite(numericValue) ? numericValue : 0;
  return unit === 'km' ? safeValue / KM_PER_MILE : safeValue;
};

export const getDistanceUnitLabel = (unit: DistanceUnit = getSavedDistanceUnit(), long = false): string => {
  if (unit === 'km') {
    return long ? 'kilometers' : 'km';
  }

  return long ? 'miles' : 'mi';
};

export const formatDistance = (
  miles: number | null | undefined,
  options: {
    unit?: DistanceUnit;
    maximumFractionDigits?: number;
    includeUnit?: boolean;
    longUnit?: boolean;
  } = {}
): string => {
  const unit = options.unit || getSavedDistanceUnit();
  const value = toDisplayDistance(miles, unit);
  const formattedValue = value.toLocaleString(undefined, {
    maximumFractionDigits: options.maximumFractionDigits ?? 0
  });

  if (options.includeUnit === false) {
    return formattedValue;
  }

  return `${formattedValue} ${getDistanceUnitLabel(unit, options.longUnit)}`;
};

export const formatCostPerDistance = (
  costPerMile: number | null | undefined,
  options: {
    unit?: DistanceUnit;
    maximumFractionDigits?: number;
  } = {}
): string => {
  const unit = options.unit || getSavedDistanceUnit();
  const safeCost = Number.isFinite(Number(costPerMile)) ? Number(costPerMile) : 0;
  const costPerSelectedUnit = unit === 'km' ? safeCost / KM_PER_MILE : safeCost;

  return `$${costPerSelectedUnit.toFixed(options.maximumFractionDigits ?? 2)}/${getDistanceUnitLabel(unit)}`;
};

export const getMileageCsvHeader = (unit: DistanceUnit = getSavedDistanceUnit()): string =>
  `Mileage (${getDistanceUnitLabel(unit)})`;

export const readMileageFromCsvRecord = (
  record: CsvRecord,
  unit: DistanceUnit = getSavedDistanceUnit(),
  fallback = 0
): number => {
  const kilometerValue = getCsvCell(record, ['Mileage (km)', 'Odometer (km)', 'Kilometers', 'Kilometres', 'KM']);
  if (kilometerValue) {
    return Math.max(0, Math.trunc(fromDisplayDistance(parseNumber(kilometerValue, fallback), 'km')));
  }

  const mileValue = getCsvCell(record, ['Mileage (mi)', 'Odometer (mi)', 'Miles', 'MI']);
  if (mileValue) {
    return Math.max(0, Math.trunc(parseNumber(mileValue, fallback)));
  }

  const currentUnitValue = getCsvCell(record, ['Mileage', 'Odometer']);
  if (currentUnitValue) {
    return Math.max(0, Math.trunc(fromDisplayDistance(parseNumber(currentUnitValue, fallback), unit)));
  }

  return fallback;
};

export const useDistanceUnit = (): DistanceUnit => {
  const [distanceUnit, setDistanceUnit] = useState<DistanceUnit>(() => getSavedDistanceUnit());

  useEffect(() => {
    const syncDistanceUnit = () => {
      setDistanceUnit(getSavedDistanceUnit());
    };

    window.addEventListener('storage', syncDistanceUnit);
    window.addEventListener(DISTANCE_UNIT_CHANGE_EVENT, syncDistanceUnit);

    return () => {
      window.removeEventListener('storage', syncDistanceUnit);
      window.removeEventListener(DISTANCE_UNIT_CHANGE_EVENT, syncDistanceUnit);
    };
  }, []);

  return distanceUnit;
};
